import React, { useRef, ChangeEvent, DragEvent } from "react";
import styles from "./FileUpload.module.css";

export type FileType = "image" | "document" | "video" | "audio";

interface FileTypeConfig {
  accept: string;
  maxSize: number; // в байтах
  label: string;
}

const FILE_TYPE_CONFIG: Record<FileType, FileTypeConfig> = {
  image: {
    accept: ".jpg,.jpeg,.png,.gif,.webp",
    maxSize: 5 * 1024 * 1024, // 5MB
    label:
      "Добавьте фотографии, перетащив их в эту область, или нажмите на нее для их выбора",
  },
  document: {
    accept: ".pdf,.doc,.docx,.txt",
    maxSize: 10 * 1024 * 1024, // 10MB
    label:
      "Добавьте файлы, перетащив их в эту область, или нажмите на нее для их выбора",
  },
  video: {
    accept: ".mp4,.webm,.avi",
    maxSize: 100 * 1024 * 1024, // 100MB
    label:
      "Добавьте видео, перетащив в эту область, или нажмите на нее для выбора",
  },
  audio: {
    accept: ".mp3,.wav,.ogg",
    maxSize: 20 * 1024 * 1024, // 20MB
    label:
      "Добавьте аудио, перетащив в эту область, или нажмите на нее для выбора",
  },
};

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  type: FileType;
  onError?: (error: string) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  type,
  onError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const config = FILE_TYPE_CONFIG[type];

  const validateFile = (file: File): boolean => {
    // Проверка размера файла
    if (file.size > config.maxSize) {
      onError?.(
        `File ${file.name} is too large. Maximum size is ${
          config.maxSize / (1024 * 1024)
        }MB`
      );
      return false;
    }

    // Проверка расширения файла
    const allowedExtensions = config.accept.split(",");
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      onError?.(
        `File ${file.name} has invalid format. Allowed formats: ${config.accept}`
      );
      return false;
    }

    return true;
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles = selectedFiles.filter(validateFile);
      onFilesChange([...files, ...validFiles]);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add(styles.dragOver);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove(styles.dragOver);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove(styles.dragOver);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(validateFile);
    onFilesChange([...files, ...validFiles]);
  };

  const removeFile = (indexToRemove: number) => {
    onFilesChange(files.filter((_, index) => index !== indexToRemove));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div
      className={styles.uploadArea}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={config.accept}
        multiple
        className={styles.hidden}
      />
      <p className={styles.uploadText}>{config.label}</p>

      {files.length > 0 && (
        <div className={styles.fileList}>
          {files.map((file, index) => (
            <div key={index} className={styles.fileItem}>
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.fileSize}>
                ({formatFileSize(file.size)})
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                className={styles.removeButton}
                aria-label="Remove file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
